import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, throwError } from 'rxjs';
import { catchError, finalize, takeUntil, tap } from 'rxjs/operators';
import { AuthService, NotificationService } from '../../../core/services';
import { SsoProviderId } from '../../../core/config/sso-providers';

@Component({
  selector: 'app-login',
  standalone: false,
  templateUrl: './login.html',
  styleUrl: './login.scss',
})
export class Login implements OnInit, OnDestroy {
  private readonly _destroy$ = new Subject<void>();

  isLoading = false;
  showPassword = false;

  form: FormGroup;

  // Set once login() comes back asking for a 2FA code instead of signing in outright.
  challengeToken: string | null = null;
  challengeExpiresInMinutes = 0;
  twoFactorCode = '';
  isVerifying = false;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private notificationService: NotificationService,
    private router: Router,
    private route: ActivatedRoute,
  ) {
    this.form = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required]],
    });
  }

  /** startSso() sends the browser away entirely, so a failed SSO
   *  attempt can't surface as anything in-memory the way form errors
   *  do it can only come back as a query param on the redirect back to
   *  this same page. Backend owns the actual param name/values; adjust
   *  this once confirmed. */
  ngOnInit(): void {
    if (this.route.snapshot.queryParamMap.get('sso_error')) {
      this.notificationService.error('Sign-in failed. Please try again or use your email and password.');
    }
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  signInWithSso(provider: SsoProviderId): void {
    this.authService.startSso(provider);
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isLoading = true;
    this.authService.login(this.form.value)
      .pipe(
        takeUntil(this._destroy$),
        catchError(error => {
          const code = error?.error?.error?.code;
          const message = code === 'email_not_confirmed'
            ? 'Please confirm your email address before signing in.'
            : 'Invalid email or password. Please try again.';
          this.notificationService.error(message);
          return throwError(() => error);
        }),
        tap(result => {
          if ('twoFactorRequired' in result) {
            this.challengeToken = result.challengeToken;
            this.challengeExpiresInMinutes = Math.round(result.expiresIn / 60);
            this.twoFactorCode = '';
          } else {
            this.router.navigate(['/']);
          }
        }),
        finalize(() => { this.isLoading = false; }),
      ).subscribe();
  }

  /** Auto-submits once a full 6-digit app code lands the digit check keeps a
   *  backup code (longer, and not purely numeric) from misfiring mid-entry. */
  onTwoFactorCodeChange(value: string): void {
    this.twoFactorCode = value;
    if (/^\d{6}$/.test(value.trim())) this.submitTwoFactorCode();
  }

  submitTwoFactorCode(): void {
    if (this.isVerifying || !this.challengeToken) return;

    if (!this.twoFactorCode.trim()) {
      this.notificationService.error('Enter your verification code.');
      return;
    }

    this.isVerifying = true;
    this.authService.completeTwoFactorChallenge({ challengeToken: this.challengeToken, code: this.twoFactorCode.trim() })
      .pipe(
        takeUntil(this._destroy$),
        catchError(error => {
          const code = error?.error?.error?.code;
          const message = code === 'challenge_expired'
            ? 'That code expired. Please sign in again.'
            : 'Incorrect code. Please try again.';
          this.notificationService.error(message);
          if (code === 'challenge_expired') this.cancelTwoFactorChallenge();
          return throwError(() => error);
        }),
        tap(() => { this.router.navigate(['/']); }),
        finalize(() => { this.isVerifying = false; }),
      ).subscribe();
  }

  /** Drops back to the email/password step the fields there are untouched,
   *  so signing in again just issues a fresh challenge if 2FA is still needed. */
  cancelTwoFactorChallenge(): void {
    this.challengeToken = null;
    this.twoFactorCode = '';
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }
}
